import React from "react";
import { Logo } from "./ui";

/* -------------------------------------------------------------------------- */
/* Términos de Uso públicos (/terms)                                          */
/* -------------------------------------------------------------------------- */

const lastUpdated = "30 de abril de 2026";

export default function Terms() {
  return (
    <div
      className="min-h-screen bg-[#FBFCFF] text-[#0B2D4D]"
      data-testid="terms-screen"
    >
      <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 md:px-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-[#0B2D4D] shadow-sm transition hover:bg-slate-50"
            data-testid="terms-back-home"
          >
            <span aria-hidden="true">←</span> Volver al inicio
          </a>
          <Logo size="sm" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-10 md:px-8 md:pt-14">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#FF5C5C]">
          PawCita
        </p>
        <h1 className="mt-2 font-display text-4xl font-black leading-tight md:text-5xl">
          Términos de Uso
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          Última actualización: {lastUpdated}
        </p>

        <div className="mt-8 space-y-6 text-base leading-relaxed text-slate-700">
          <p>
            PawCita es una plataforma de conexión entre padres de perros para
            coordinar encuentros responsables. Al usar PawCita aceptas estos
            términos. Si no estás de acuerdo, te pedimos no utilizar la app.
          </p>

          <Section title="1. Qué es PawCita">
            <p>
              PawCita facilita el contacto entre dueños de perros mediante
              perfiles, solicitudes y un chat protegido. No reemplaza el
              consejo profesional de un médico veterinario, ni garantiza
              resultados de compatibilidad, cruce o reproducción.
            </p>
          </Section>

          <Section title="2. Responsabilidad del usuario">
            <ul className="ml-5 list-disc space-y-1">
              <li>
                Eres responsable de la veracidad de la información que publicas
                sobre ti y tu perro (raza, edad, salud, vacunas, ubicación,
                disponibilidad).
              </li>
              <li>
                Eres responsable de coordinar encuentros de manera segura y de
                cumplir con la legislación local sobre tenencia y reproducción
                de animales.
              </li>
              <li>
                Recomendamos siempre que el primer encuentro ocurra en un
                lugar público.
              </li>
              <li>
                Recomendamos consultar con un médico veterinario antes de
                cualquier cruce.
              </li>
            </ul>
          </Section>

          <Section title="3. Limitación de responsabilidad">
            <p>
              PawCita <strong>no es responsable</strong> por:
            </p>
            <ul className="ml-5 list-disc space-y-1">
              <li>Decisiones de cruce o reproducción tomadas por los usuarios.</li>
              <li>Estado de salud, compatibilidad o temperamento de los perros.</li>
              <li>Costos veterinarios, prenatales, postnatales o de cualquier tipo.</li>
              <li>Nacimiento, cuidado o adopción de crías.</li>
              <li>Acuerdos económicos, contractuales o personales entre usuarios.</li>
              <li>Daños o lesiones derivados de los encuentros.</li>
            </ul>
          </Section>

          <Section title="4. Conductas prohibidas">
            <p>Está prohibido usar PawCita para:</p>
            <ul className="ml-5 list-disc space-y-1">
              <li>Promover reproducción irresponsable o lucro por crías.</li>
              <li>Maltrato o explotación animal en cualquier forma.</li>
              <li>Publicar información falsa o engañosa.</li>
              <li>Acoso, intimidación o discriminación a otros usuarios.</li>
              <li>Fraudes, estafas o solicitudes de pago indebidas.</li>
              <li>
                Compartir datos personales (teléfonos, correos, redes sociales,
                URLs externas) fuera del chat de PawCita.
              </li>
            </ul>
          </Section>

          <Section title="5. Moderación">
            <p>
              PawCita puede limitar, suspender o bloquear a usuarios que
              incumplan estos términos o demuestren conductas inseguras.
              También podemos eliminar contenidos que violen la política de
              bienestar animal o nuestra Política de Privacidad.
            </p>
          </Section>

          <Section title="6. Comunicación dentro de la app">
            <p>
              Para tu seguridad, el chat bloquea automáticamente teléfonos,
              correos electrónicos, URLs y referencias a redes sociales. Por
              favor mantén toda la comunicación dentro de PawCita hasta que
              hayas verificado por tu cuenta a la otra persona.
            </p>
          </Section>

          <Section title="7. Cambios a estos términos">
            <p>
              Podremos actualizar estos términos a medida que PawCita
              evolucione. Publicaremos los cambios en esta misma página y
              actualizaremos la fecha de última modificación.
            </p>
          </Section>

          <Section title="8. Contacto">
            <p>Para reportes, consultas o sugerencias:</p>
            <p className="font-bold text-[#0B2D4D]">contacto@pawcita.app</p>
          </Section>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-6 text-sm">
          <a href="/" className="font-bold text-[#0B2D4D] hover:text-[#FF5C5C]">
            ← Volver al inicio
          </a>
          <a
            href="/privacy"
            className="font-bold text-[#0B2D4D] hover:text-[#FF5C5C]"
            data-testid="terms-link-to-privacy"
          >
            Política de Privacidad →
          </a>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-black text-[#0B2D4D]">
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
